import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";

export interface PresenceUser {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  status: "online" | "typing" | "idle";
  current_action?: string;
  joined_at: string;
}

interface UsePresenceOptions {
  channelName: string;
  enabled?: boolean;
}

export function usePresence({ channelName, enabled = true }: UsePresenceOptions) {
  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const channelRef = useRef<any>(null);
  const statusRef = useRef<string>("online");

  useEffect(() => {
    if (!enabled) return;

    let mounted = true;

    const setup = async () => {
      const user = await getSessionUser();
      if (!user || !mounted) return;
      setCurrentUserId(user.id);

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();

      const presenceData: PresenceUser = {
        user_id: user.id,
        full_name: profile?.full_name || null,
        avatar_url: profile?.avatar_url || null,
        status: "online",
        joined_at: new Date().toISOString(),
      };

      const channel = supabase.channel(`presence-${channelName}`, {
        config: { presence: { key: user.id } },
      });

      channel
        .on("presence", { event: "sync" }, () => {
          const state = channel.presenceState();
          const users: PresenceUser[] = [];
          for (const [, presences] of Object.entries(state)) {
            const p = (presences as any[])[0];
            if (p) users.push(p as PresenceUser);
          }
          if (mounted) setOnlineUsers(users);
        })
        .subscribe(async (status) => {
          if (status === "SUBSCRIBED") {
            await channel.track(presenceData);
          }
        });

      channelRef.current = channel;
    };

    setup();

    return () => {
      mounted = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [channelName, enabled]);

  const updateStatus = useCallback(
    async (status: "online" | "typing" | "idle", action?: string) => {
      if (!channelRef.current) return;
      statusRef.current = status;
      const state = channelRef.current.presenceState();
      const myPresence = currentUserId ? (state[currentUserId] as any[])?.[0] : null;
      if (myPresence) {
        await channelRef.current.track({
          ...myPresence,
          status,
          current_action: action || myPresence.current_action,
        });
      }
    },
    [currentUserId]
  );

  const otherUsers = onlineUsers.filter((u) => u.user_id !== currentUserId);

  return { onlineUsers, otherUsers, currentUserId, updateStatus };
}
